// paymentHelpers.ts

import { CoachProfileModel } from "app/models";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";

export const OnlinePaymentTypeOptions = [
  { value: 'send_invoice', label: 'Send Invoice' },
  { value: 'card_on_file', label: 'Charge Card on File' },
  { value: 'checkout', label: 'Checkout' },
];

export const PaymentStatusOptions = [
  { value: 'unpaid', label: 'Unpaid', color: '#FFA000' },
  { value: 'paid', label: 'Paid', color: '#43A047' },
  { value: 'failed', label: 'Failed', color: '#E54500' },
  { value: 'past_due', label: 'Past Due', color: '#E54500' },
];

// CN-2515: `includeAllNow` adds the legacy bill-everything-upfront option
// ('all_immediate' — one shared invoice/charge covering the whole series).
// Only offered while creating an event with a repeating rule.
export const getChargeTimingOptions = (onlinePaymentType?: string, includeAllNow = false) => {
  if (onlinePaymentType === 'send_invoice') {
    return [
      { value: 'immediate', label: 'Send Invoice Now' },
      ...(includeAllNow ? [{ value: 'all_immediate', label: 'Send All Invoices Now' }] : []),
      { value: 'after_session', label: 'Send After the Session' },
    ];
  }
  return [
    { value: 'immediate', label: 'Charge Now' },
    ...(includeAllNow ? [{ value: 'all_immediate', label: 'Charge All Now' }] : []),
    { value: 'after_session', label: 'Charge After Session' },
  ];
};

export const ChargeTimingOptions = getChargeTimingOptions();

interface AvailablePackage {
  rateLinkId: string;
}

interface PaymentMethodParams {
  lessonRateOptions: LessonRateModel[];
  watchLessonRateId: number;
  selectedAthletes: any[];
  availablePackages?: AvailablePackage[];
  coachProfile: CoachProfileModel;
}

interface PaymentMethodResult {
  paymentMethod: "offline" | "online" | "package";
  selectedPackageId: string | null;
}

export const getAutoSelectedPaymentMethod = ({
  lessonRateOptions,
  watchLessonRateId,
  selectedAthletes,
  availablePackages,
  coachProfile,
}: PaymentMethodParams): PaymentMethodResult => {
  const sessionType = lessonRateOptions.find(
    (lr) => lr.id === watchLessonRateId,
  );

  const hasPackages = availablePackages && availablePackages.length > 0;
  const showSubscriptionOption =
    selectedAthletes.length === 1 && hasPackages;
  const showOnlineOption =
    coachProfile?.allowsPayments && sessionType?.onlinePaymentsActive !== false;
  const showOfflineOption = true;

  let defaultMethod: "offline" | "online" | "package" = "offline";
  let selectedPackageId: string | null = null;

  // Priority order:
  // 1. Package (if available and single athlete)
  // 2. Session's default (online/offline)
  // 3. Fallback to what's available

  if (showSubscriptionOption) {
    defaultMethod = "package";
    selectedPackageId = availablePackages[0]?.rateLinkId ?? null;
  } else {
    if (sessionType?.onlineDefault && showOnlineOption) {
      defaultMethod = "online";
    } else if (!sessionType?.onlineDefault && showOfflineOption) {
      defaultMethod = "offline";
    } else if (showOnlineOption) {
      defaultMethod = "online";
    } else {
      defaultMethod = "offline";
    }
  }

  return {
    paymentMethod: defaultMethod,
    selectedPackageId,
  };
};

// CN-2515: creating a repeating series with online payment always shows a
// confirmation popup explaining when money moves, with copy tailored to the
// charge-timing choice (immediate / all_immediate / after_session). The coach
// must confirm that behavior before the series is created.
export const needsBillNowConfirmation = (data: any, isNew: boolean): boolean =>
  isNew &&
  data.type === 'lesson_schedule' &&
  data.payment_method === 'online' &&
  data.online_payment_type !== 'checkout' &&
  !!data.repeat;
